import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Divider,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { cloneDeep } from "lodash";
import PresentationFormDialog from "../sections/presentations/PresentationFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";

const PAGE_SIZE = 20;
const defaultFilter = { name: "", isActive: "" };

export default function PresentationsPage() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [filters, setFilters] = useState(defaultFilter);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPresentation, setSelectedPresentation] = useState(null);

  const handleReset = () => setFilters(cloneDeep(defaultFilter));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (presentation) => {
    setSelectedPresentation(presentation);
    setDialogOpen(true);
  };

  const handleMenuOpen = (event, presentation) => {
    setAnchorEl(event.currentTarget);
    setSelectedPresentation(presentation);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleToggleStatusClick = () => {
    handleMenuClose();
    setConfirmOpen(true);
  };

  const handleToggleStatus = async () => {
    if (!selectedPresentation) return;
    try {
      const newStatus = !selectedPresentation.isActive;
      const res = await fetchWithInterceptor(
        `/api/presentations/${selectedPresentation.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
          body: JSON.stringify({ isActive: newStatus }),
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed updating presentation status");
      }
      appContext.showMessage(
        newStatus ? "Presentation activated" : "Presentation deactivated",
        "success"
      );
      handleMenuClose();
      setSelectedPresentation(null);
      loadPresentations(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error updating presentation status", "error");
      handleMenuClose();
      setSelectedPresentation(null);
    }
  };

  const handleSavePresentation = async (payload) => {
    try {
      const method = selectedPresentation ? "PUT" : "POST";
      const url = selectedPresentation
        ? `/api/presentations/${selectedPresentation.id}`
        : "/api/presentations";

      const res = await fetchWithInterceptor(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to save presentation");
      }

      appContext.showMessage(
        selectedPresentation ? "Presentation updated successfully" : "Presentation created successfully",
        "success"
      );

      setDialogOpen(false);
      setSelectedPresentation(null);
      loadPresentations(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error saving presentation", "error" );
    }
  };

  const loadPresentations = async (resetPage = false) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.name) params.append("name", filters.name);
      if (filters.isActive !== "") params.append("isActive", filters.isActive);
      params.append("limit", PAGE_SIZE);
      params.append(
        "offset",
        String(resetPage ? 0 : paginationModel.page * PAGE_SIZE)
      );

      const response = await fetchWithInterceptor(
        `/api/presentations?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch presentations");

      const data = await response.json();
      setRows(data.rows || []);
      setRowCount(data.total ?? 0);

      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading presentations", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPresentations(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadPresentations(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = [
    {
      field: "name",
      headerName: "NAME",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "isActive",
      headerName: "STATUS",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? "Active" : "Inactive"}
          color={params.row.isActive ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <>
          <Button
            size="small"
            variant="text"
            sx={{ mr: 1, minWidth: "auto", p: 0.5 }}
            onClick={() => handleEdit(params.row)}
          >
            EDIT
          </Button>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and deactivate presentations (e.g., "Single" / "6 Pack"). Changes made here will be consumed by other modules in the ZYN web app (e.g., product creation).
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper sx={{ width: "100%", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="Name"
                  name="name"
                  fullWidth
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  label="Status"
                  name="isActive"
                  fullWidth
                  value={filters.isActive}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid
                size={{ xs: 12, md: 3 }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>

          <Divider />
            <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedPresentation(null);
                  setDialogOpen(true);
                }}
              >
                CREATE PRESENTATION
              </Button>
            </Grid>

          <DataGrid
            rows={rows}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            loading={loading}
            getRowId={(row) => row.id}
            disableColumnFilter
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
              },
            }}
          />
          <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleMenuClose}>
            <MenuItem onClick={handleToggleStatusClick}>
              {selectedPresentation?.isActive ? "Deactivate" : "Activate"}
            </MenuItem>
          </Menu>
        </Paper>
      </Container>

      <PresentationFormDialog
        open={dialogOpen}
        rowId={selectedPresentation?.id}
        presentation={selectedPresentation}
        onClose={() => {
          setDialogOpen(false);
          setSelectedPresentation(null);
        }}
        onSave={handleSavePresentation}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={
          selectedPresentation?.isActive
            ? "Deactivate Presentation"
            : "Activate Presentation"
        }
        message={
          selectedPresentation?.isActive ? (
            <>
              Are you sure you want to deactivate
              <strong style={{ fontWeight: 500 }}>
                {" "}
                &quot;{selectedPresentation?.name}&quot;?
              </strong>
            </>
          ) : (
            <>
              Are you sure you want to activate
              <strong style={{ fontWeight: 500 }}>
                {" "}
                &quot;{selectedPresentation?.name}&quot;?
              </strong>
            </>
          )
        }
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setConfirmOpen(false);
          await handleToggleStatus();
        }}
      />
    </>
  );
}
